import { useState } from 'react';
import { Icon } from '@/components/Icon';
import {
  annualPerMonth,
  annualPrice,
  annualSavingAmount,
  annualSavingPct,
  formatPrice,
  isSoldIn,
  monthlyPrice,
  type CurrencyCode,
} from '@/lib/pricing';
import { TIERS, type Tier } from '@/lib/tier';
import './PlanCards.css';

/**
 * The three self-serve plan cards.
 *
 * Shared by the in-app Pricing surface and onboarding screen 2, so the two can never drift.
 *
 * **Three cards in a three-column grid — never four.** An earlier version left a stray empty
 * cell when a fourth tier was removed, which rendered as an unexplained gray box. Team is a
 * banner below, not a card.
 */

export type BillingPeriod = 'monthly' | 'annual';

const ORDER: Tier[] = ['snapshot', 'intelligence', 'pro'];

/** Per-feature explanations. Click-to-toggle, so they work on touch. */
const TIPS: Record<string, string> = {
  explain:
    'Aware Explain rewrites a meeting recap in plain language, so you can understand what happened without reading the whole thing. One use per recap, and you can top up in packs.',
  ask: 'Ask Aware answers questions in plain language across every place we cover, and cites the exact meeting passages it used.',
  summaries:
    'Every meeting in the places you follow is written up into agenda items with speakers and votes. Never metered.',
  search:
    'Search every meeting we hold in one query, with filters for state, city, body type, date and topic, and the matched passage highlighted.',
  signals:
    'A signal is a saved search that keeps working. It fills your feed with new matches and builds Legislative History records for you.',
  leg: 'Follow one ordinance or resolution across every meeting that touched it — first mention, readings and final vote — in a single timeline.',
  towns:
    'A place is a jurisdiction you follow — a town, a county board or a state agency. A town covers both its council and its school board. You can swap which places you follow whenever you like.',
  wa: 'Get new-meeting alerts on WhatsApp as well as email, with the body type named so you know the source at a glance.',
};

interface Feature {
  label: string;
  tip?: keyof typeof TIPS;
}

const FEATURES: Record<Tier, Feature[]> = {
  snapshot: [
    { label: 'One place — council and school board', tip: 'towns' },
    { label: 'Plain-English recaps', tip: 'summaries' },
    { label: '100 Aware Explain uses a month', tip: 'explain' },
    { label: 'Email alerts' },
  ],
  intelligence: [
    { label: 'Up to 3 places', tip: 'towns' },
    { label: '400 Aware Explain uses a month', tip: 'explain' },
    { label: 'Cross-city Search', tip: 'search' },
    { label: 'Signals', tip: 'signals' },
    { label: 'WhatsApp alerts', tip: 'wa' },
  ],
  pro: [
    { label: 'Unlimited places', tip: 'towns' },
    { label: '800 Aware Explain uses a month', tip: 'explain' },
    { label: '200 Ask Aware questions a month', tip: 'ask' },
    { label: 'Legislative History', tip: 'leg' },
    { label: 'Topic Trends' },
  ],
};

export function PlanCards({
  currency,
  period,
  ctaLabel = 'START FOR FREE',
  currentTier,
  onChoose,
}: {
  currency: CurrencyCode;
  period: BillingPeriod;
  ctaLabel?: string;
  /** Marks the plan the customer is already on, where this is shown in-app. */
  currentTier?: Tier;
  onChoose: (tier: Tier) => void;
}) {
  const [openTip, setOpenTip] = useState<string | null>(null);

  return (
    <div className="cards">
      {ORDER.map((tier) => {
        const plan = TIERS[tier];
        const sold = isSoldIn(tier, currency);
        const monthly = monthlyPrice(currency, tier);
        const annual = annualPrice(currency, tier);
        const isCurrent = currentTier === tier;

        return (
          <article key={tier} className="card" data-unavailable={!sold} data-current={isCurrent}>
            <h3 className="card__name">{plan.label}</h3>

            {/* Snapshot is not sold outside the US and Canada. The card is neither hidden nor
                priced — it goes to an explicit unavailable state with a route onward. */}
            {!sold ? (
              <>
                <p className="card__unavailable">
                  Not available in {currency}. Snapshot is sold in the US and Canada only.
                </p>
                <button
                  type="button"
                  className="card__cta card__cta--quiet"
                  onClick={() => onChoose('intelligence')}
                >
                  See Intelligence instead
                </button>
              </>
            ) : (
              <>
                <p className="card__price">
                  {period === 'annual'
                    ? formatPrice(currency, Math.round(annualPerMonth(currency, tier)))
                    : formatPrice(currency, monthly)}
                  <span className="card__per">/mo</span>
                </p>

                {period === 'annual' ? (
                  /* The real annual total and the real saving, not just a per-month figure. */
                  <p className="card__annual">
                    {formatPrice(currency, annual)} billed yearly — save{' '}
                    {formatPrice(currency, annualSavingAmount(currency, tier))} (
                    {annualSavingPct(currency, tier)}%)
                  </p>
                ) : (
                  <p className="card__annual">Billed monthly. Cancel any time.</p>
                )}

                <button
                  type="button"
                  className="card__cta"
                  disabled={isCurrent}
                  onClick={() => onChoose(tier)}
                >
                  {isCurrent ? 'Your current plan' : ctaLabel}
                </button>
              </>
            )}

            <ul className="card__features">
              {FEATURES[tier].map((feature) => {
                const tipId = feature.tip ? `${tier}-${feature.tip}` : null;
                return (
                  <li key={feature.label}>
                    <span className="card__featureRow">
                      <Icon name="check" className="card__tick" />
                      <span>{feature.label}</span>
                      {feature.tip && (
                        <button
                          type="button"
                          className="card__info"
                          aria-expanded={openTip === tipId}
                          aria-label={`What is ${feature.label}?`}
                          onClick={() => setOpenTip(openTip === tipId ? null : tipId)}
                        >
                          i
                        </button>
                      )}
                    </span>
                    {feature.tip && openTip === tipId && (
                      <p className="card__tip">{TIPS[feature.tip]}</p>
                    )}
                  </li>
                );
              })}
            </ul>
          </article>
        );
      })}
    </div>
  );
}
